import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Typography, Tag, Empty, Spin, Button, Space, Descriptions
} from 'antd';
import { TeamOutlined, DollarOutlined, UserOutlined, RiseOutlined, ArrowLeftOutlined, LineChartOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgentTierDemo from './admin/AgentTierDemo';

axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;

const TsAgentPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);

  // 点上级/下级代理名字 → 进入私信对话
  const openMsg = (peer) => {
    if (!peer?.id) return;
    navigate('/messages', { state: { peer } });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/ts/portal/dashboard', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setData(res.data.dashboard);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const d = data || {};

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回</Button>
        <Title level={3} style={{ margin: 0 }}>TS 代理平台</Title>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="我的客户" value={d.myCustomerCount || 0} prefix={<TeamOutlined />} suffix="人" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="客户 TS 金额" value={d.customerTsAmount || 0} prefix={<DollarOutlined />} precision={0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="下级代理" value={d.downlineCount || 0} prefix={<UserOutlined />} suffix="人" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic title="下级推荐总额" value={d.downlineTsTotal || 0} prefix={<RiseOutlined />} precision={0} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
      </Row>

      {!d.isAgent && !d.isCustomer && (
        <Card>
          <Empty description="您尚未加入 TS 代理平台，请联系管理员在后台将您的账号关联为客户或代理人" />
        </Card>
      )}

      {/* 我推荐的客户 */}
      {d.isAgent && (
        <Card title={`我推荐的客户（${d.myCustomerCount || 0} 人 · 合计 ¥${parseFloat(d.customerTsAmount || 0).toFixed(0)}）`} style={{ marginBottom: 16 }}>
          {d.myCustomers?.length > 0 ? (
            <Table
              rowKey="id"
              size="small"
              tableLayout="fixed"
              dataSource={d.myCustomers}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              columns={[
                { title: '客户姓名', dataIndex: 'customer_name', ellipsis: true },
                { title: '手机号', dataIndex: 'phone', ellipsis: true, render: (v) => v || '-' },
                { title: 'TS 金额', dataIndex: 'ts_amount', align: 'right', render: (v) => <Text strong style={{ color: '#6366F1', whiteSpace: 'nowrap' }}>¥{parseFloat(v || 0).toFixed(0)}</Text> },
              ]}
            />
          ) : (
            <Empty description="暂无推荐客户" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      )}

      {/* 我名下的代理 */}
      {d.isAgent && (
        <Card title={`下级代理（${d.downlineCount || 0} 人）`} style={{ marginBottom: 16 }}>
          {d.myDownlineAgents?.length > 0 ? (
            <Table
              rowKey="id"
              size="small"
              tableLayout="fixed"
              dataSource={d.myDownlineAgents}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              columns={[
                { title: '代理人', dataIndex: 'agent_name', ellipsis: true, render: (name, r) => (
                  r.user_id
                    ? <a onClick={() => openMsg({ id: r.user_id, nickname: name })}><MessageOutlined style={{ marginInlineEnd: 4 }} />{name}</a>
                    : name
                ) },
                { title: '级别', dataIndex: 'agent_level', width: 78, render: (l) => <Tag color="blue" style={{ marginInlineEnd: 0 }}>{(l || '').replace('代理', '')}</Tag> },
                { title: '客户', dataIndex: 'customer_count', width: 48, align: 'center', render: (v) => `${v || 0}` },
                { title: 'TS 金额', dataIndex: 'referred_ts_total', align: 'right', render: (v) => <Text strong style={{ color: '#1890ff', whiteSpace: 'nowrap' }}>¥{parseFloat(v || 0).toFixed(0)}</Text> },
              ]}
            />
          ) : (
            <Empty description="暂无下级代理" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      )}

      {/* 我作为客户的资料 */}
      {d.isCustomer && (
        <Card title="我的 TS 客户资料" style={{ marginBottom: 16 }}>
          <Descriptions column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="客户姓名">{d.customerProfile?.customer_name}</Descriptions.Item>
            <Descriptions.Item label="地区">{d.customerProfile?.region || '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{d.customerProfile?.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="客户类型"><Tag>{d.customerProfile?.customer_type}</Tag></Descriptions.Item>
            <Descriptions.Item label="我的 TS 金额">¥{parseFloat(d.myTsAmount || 0).toFixed(0)}</Descriptions.Item>
            <Descriptions.Item label="推荐人">{d.myReferrer || '无'}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* 我的资料 */}
      {d.isAgent && d.agentProfile && (
        <Card
          title="我的资料"
          extra={<Button type="primary" size="small" icon={<LineChartOutlined />} onClick={() => setDemoOpen(true)}>利益演示</Button>}
        >
          <Descriptions column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="代理人姓名">{d.agentProfile.agent_name}</Descriptions.Item>
            <Descriptions.Item label="代理级别"><Tag color="gold">{d.agentProfile.agent_level}</Tag></Descriptions.Item>
            <Descriptions.Item label="上级代理">
              {d.upline
                ? (d.upline.user_id
                    ? <a onClick={() => openMsg({ id: d.upline.user_id, nickname: d.upline.name, avatar: d.upline.avatar })}><MessageOutlined style={{ marginInlineEnd: 4 }} />{d.upline.name}</a>
                    : d.upline.name)
                : '无'}
            </Descriptions.Item>
            <Descriptions.Item label="总推荐金额">¥{parseFloat(d.customerTsAmount || 0).toFixed(0)}</Descriptions.Item>
            <Descriptions.Item label={`推荐费（${((d.feeRate || 0) * 100).toFixed(0)}%）`}>
              <Text strong style={{ color: '#722ed1' }}>¥{parseFloat(d.referralFee || 0).toFixed(0)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="推荐代理奖励">
              <Text strong style={{ color: '#eb2f96' }}>¥{parseFloat(d.recruitReward || 0).toFixed(0)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={`下级代理贡献的奖励（${((d.downRate || 0) * 100).toFixed(0)}%）`}>
              <Text strong style={{ color: '#13c2c2' }}>¥{parseFloat(d.downlineReward || 0).toFixed(0)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="总收益" span={2}>
              <Text strong style={{ color: '#f5222d', fontSize: 16, whiteSpace: 'nowrap' }}>¥{parseFloat(d.totalReward || 0).toFixed(0)}</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>（推荐费 + 推荐代理奖励 + 下级贡献奖励）</div>
            </Descriptions.Item>
            <Descriptions.Item label="已结算收益">¥{parseFloat(d.settledFee || 0).toFixed(0)}</Descriptions.Item>
            <Descriptions.Item label="未结算收益">
              <Text strong style={{ color: '#fa8c16' }}>¥{parseFloat(d.unsettledFee || 0).toFixed(0)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <AgentTierDemo open={demoOpen} onClose={() => setDemoOpen(false)} admin={!!d.isAdmin} />
    </div>
  );
};

export default TsAgentPortal;
